import { EvidenceBlob } from './martech-scanner-constants';

/**
 * JavaScript-rendering dependency, estimated per page from the raw HTML the
 * crawl fetched (no browser): how much readable text and how many links exist
 * BEFORE any JavaScript runs. A page whose initial HTML is a near-empty app
 * shell depends on JS for its primary content — which is what crawlers and
 * LLM agents that don't execute JS will (not) see. Heuristic by design; the
 * runtime-browser engine is what would measure the rendered/raw delta exactly.
 */

export const RenderVerdict = {
  SERVER_RENDERED: 'server-rendered',
  PARTIAL: 'partial',
  JS_REQUIRED: 'js-required'
} as const;
export type RenderVerdictType = (typeof RenderVerdict)[keyof typeof RenderVerdict];

export interface PageRenderCheck {
  url: string;
  /** Readable text characters in the initial HTML (tags/scripts/styles stripped). */
  textChars: number;
  /** <a href> links present in the initial HTML. */
  linkCount: number;
  verdict: RenderVerdictType;
}

// Thresholds documented on the Glossary tab.
const TEXT_SERVER_RENDERED = 2500;
const TEXT_PARTIAL = 800;
const LINKS_SERVER_RENDERED = 10;

const visibleText = (html: string): string =>
  html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z#0-9]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const verdictFor = (textChars: number, linkCount: number): RenderVerdictType => {
  if (textChars >= TEXT_SERVER_RENDERED && linkCount >= LINKS_SERVER_RENDERED) {
    return RenderVerdict.SERVER_RENDERED;
  }
  if (textChars >= TEXT_PARTIAL) {
    return RenderVerdict.PARTIAL;
  }
  return RenderVerdict.JS_REQUIRED;
};

/** One render check per html:* evidence blob (i.e. per crawled page). */
export const buildRenderChecks = (evidenceBlobs: EvidenceBlob[]): PageRenderCheck[] =>
  evidenceBlobs
    .filter(b => b.source.startsWith('html:'))
    .map(b => {
      const url = b.source.slice('html:'.length);
      const textChars = visibleText(b.text).length;
      const linkCount = (b.text.match(/<a[^>]+href=/gi) ?? []).length;
      return { url, textChars, linkCount, verdict: verdictFor(textChars, linkCount) };
    });

export interface RenderSummary {
  checks: PageRenderCheck[];
  /** Share of crawled pages whose primary content appears to need JS (0–100). */
  jsRequiredPct: number;
}

/**
 * Recomputes jsRequiredPct for an arbitrary checks list, split out from
 * summarizeRenderChecks so a full-scan merge can recompute it over the
 * *union* of a quick scan's checks and the full scan's own (see
 * full-scan-merge-utils.ts), not just one scan's own evidence.
 */
export const summarizeChecks = (checks: PageRenderCheck[]): RenderSummary => {
  const jsRequired = checks.filter(c => c.verdict === RenderVerdict.JS_REQUIRED).length;
  return {
    checks,
    jsRequiredPct: checks.length === 0 ? 0 : Math.round((100 * jsRequired) / checks.length)
  };
};

export const summarizeRenderChecks = (evidenceBlobs: EvidenceBlob[]): RenderSummary =>
  summarizeChecks(buildRenderChecks(evidenceBlobs));
